/**
 * FAQ Component with Schema.org Structured Data
 *
 * Displays frequently asked questions with collapsible answers
 * and includes JSON-LD structured data for SEO.
 *
 * Following CLAUDE.md:
 * - Uses Tailwind CSS utility classes for styling
 * - Brand-agnostic (no brand logic in components)
 * - Uses cn() utility for conditional classes
 * - Mobile-first responsive design
 * - Uses Shadcn Accordion component
 */

"use client";

import Script from "next/script";
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "@/components/ui/accordion";
import { cn } from "@/lib/utils";
import type { ComponentFAQ } from "@/lib/validation";

export interface FAQProps extends ComponentFAQ {
	className?: string;
}

/**
 * Generate Schema.org FAQPage structured data
 * FAQPage is correct even for FAQ sections within pages
 * https://schema.org/FAQPage
 * https://developers.google.com/search/docs/appearance/structured-data/faqpage
 */
function generateFAQStructuredData(faqItems: ComponentFAQ["faqItems"]) {
	return {
		"@context": "https://schema.org",
		"@type": "FAQPage",
		mainEntity: faqItems.map((item) => ({
			"@type": "Question",
			name: item.question,
			acceptedAnswer: {
				"@type": "Answer",
				text: item.answer,
			},
		})),
	};
}

/**
 * Simple hash function for generating stable keys
 */
function hashString(str: string): string {
	let hash = 0;
	for (let i = 0; i < str.length; i++) {
		const char = str.charCodeAt(i);
		hash = (hash << 5) - hash + char;
		hash = hash & hash; // Convert to 32bit integer
	}
	return Math.abs(hash).toString(36);
}

export function FAQ({ className, heading, faqItems }: FAQProps) {
	if (!faqItems || faqItems.length === 0) return null;

	const structuredData = generateFAQStructuredData(faqItems);
	// Generate unique ID for this FAQ block to support multiple FAQ sections per page
	const uniqueId = `faq-structured-data-${hashString(JSON.stringify(faqItems))}`;

	return (
		<>
			{/* Schema.org Structured Data */}
			<Script
				id={uniqueId}
				type="application/ld+json"
				// biome-ignore lint/security/noDangerouslySetInnerHtml: Structured data JSON-LD requires dangerouslySetInnerHTML
				dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
			/>

			{/* FAQ Section */}
			<section className={cn("py-12 md:py-16 lg:py-20", className)}>
				<div className="container mx-auto px-4">
					{/* Section Header */}
					{heading && (
						<div className="mb-12 text-center">
							<h2 className="text-2xl font-bold md:text-3xl lg:text-4xl">
								{heading}
							</h2>
						</div>
					)}

					{/* FAQ Accordion */}
					<div className="mx-auto max-w-3xl">
						<Accordion type="single" collapsible className="w-full">
							{faqItems.map((item, index) => (
								<AccordionItem
									key={hashString(`${item.question}-${index}`)}
									value={`item-${index}`}
									className="border-b border-border"
								>
									<AccordionTrigger className="text-left text-base font-semibold hover:no-underline md:text-lg">
										{item.question}
									</AccordionTrigger>
									<AccordionContent className="text-base text-muted-foreground">
										<div
											className="prose prose-sm max-w-none dark:prose-invert"
											// biome-ignore lint/security/noDangerouslySetInnerHtml: FAQ answers are validated content from CMS
											dangerouslySetInnerHTML={{ __html: item.answer }}
										/>
									</AccordionContent>
								</AccordionItem>
							))}
						</Accordion>
					</div>
				</div>
			</section>
		</>
	);
}
